import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Fade,
  Stack,
} from "@mui/material";
import { AutoAwesome, Close, Article } from "@mui/icons-material";
import { useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import { RequestFile, SignRequest } from "models/interfaces";
import {
  fetchDocumentSummary,
  fetchDocumentSummaryCallback,
  fetchSignedDocumentSummary,
} from "../SignRequestService";
import { handleErrorMessage, isTruthy } from "helpers/methods";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CustomAutoComplete from "global/components/CustomAutoComplete/CustomAutoComplete";
import AIDisclaimer from "./AIDisclaimer";
import { convertFileForBackend } from "screens/Shared/CommonHelper";

const Summarizer = ({
  isPublic,
  requesToken,
  signRequest,
  hideHeader,
  signed,
}: {
  isPublic?: boolean;
  requesToken?: string;
  signRequest: SignRequest;
  hideHeader?: boolean;
  /** Completed request: summarise the SIGNED copy, which also reports the signatures. */
  signed?: boolean;
}) => {
  const [selectedDocument, setSelectedDocument] = useState<RequestFile | null>(
    null,
  );
  const [summary, setSummary] = useState<any>(null);
  const [aiLoader, setAiLoader] = useState<boolean>(false);
  const [error, setError] = useState("");

  const documentOptions = useMemo(() => {
    return signRequest.files.map((file: RequestFile, index: number) => {
      const fileName =
        (signed ? file.signedPdf : undefined) ||
        file.unsignedDoc ||
        file.unsignedPdf ||
        `Document ${index + 1}`;
      return {
        id: index,
        file,
        label: fileName,
        value: fileName,
      };
    });
  }, [signRequest.files, signed]);

  const handleDocumentChange = (event: any, newValue: any) => {
    if (newValue && typeof newValue === "object" && newValue.file) {
      // Ensure we're using the file from the option
      setSelectedDocument(newValue.file);
      setSummary(null);
      setError("");
    } else if (newValue === null || newValue === undefined) {
      // Handle clearing the selection
      setSelectedDocument(null);
      setSummary(null);
      setError("");
    }
  };

  const getOptionLabel = (option: any) => {
    if (typeof option === "string") {
      return option;
    }
    return option?.label || option?.value || "";
  };

  // Get the selected option object for CustomAutoComplete
  // Must return the exact object from documentOptions array for MUI Autocomplete to work correctly
  const selectedOption = useMemo(() => {
    if (!selectedDocument) return null;

    // Get the unique identifier for the selected document
    const selectedValue =
      selectedDocument.unsignedDoc || selectedDocument.unsignedPdf;
    if (!selectedValue) return null;

    // Find the exact option object from documentOptions by matching the value
    const found = documentOptions.find(
      (opt: { id: number; file: RequestFile; label: string; value: string }) =>
        opt.value === selectedValue,
    );

    return found || null;
  }, [selectedDocument, documentOptions]);

  // Custom filter to show all options
  const filterOptions = (options: any[], state: any) => {
    return options;
  };

  const getDocumentSummary = async () => {
    if (!selectedDocument) {
      setError("Please select a document");
      return;
    }

    try {
      setAiLoader(true);
      setError("");
      const file = convertFileForBackend(selectedDocument);
      const summaryResponse = await (signed
        ? fetchSignedDocumentSummary(signRequest.id, file)
        : isPublic
          ? fetchDocumentSummaryCallback(requesToken!, file)
          : fetchDocumentSummary(signRequest.id, file));
      if (summaryResponse) {
        setSummary(summaryResponse);
      }
    } catch (error: any) {
      handleErrorMessage(error);
      setAiLoader(false);
    } finally {
      setAiLoader(false);
    }
  };

  const renderSummaryContent = (data: any, level = 0) => {
    if (!data) return null;

    return Object.entries(data).map(([key, value]) => {
      if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value)
      ) {
        return (
          <Box
            key={key}
            sx={{
              mb: level === 0 ? 3 : 2,
              mt: level === 0 ? 0 : 1,
              pl: level > 0 ? level * 0.5 : 0,
              borderLeft: level > 0 ? "2px solid rgba(0,0,0,0.1)" : "none",
              paddingLeft: level > 0 ? 2.5 : 0,
              textAlign: "left",
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                mb: 1.5,
                fontSize: level === 0 ? "0.8rem" : "0.7rem",
                color: "#333",
                lineHeight: 1,
              }}
            >
              {key}
            </Typography>
            <Box
              sx={{ pl: level === 0 ? 3 : 2.5, m: 0, listStyle: "none" }}
            >
              {renderSummaryContent(value, level + 1)}
            </Box>
          </Box>
        );
      } else if (Array.isArray(value)) {
        return (
          <Box key={key} sx={{ mb: 2, pl: level > 0 ? 2.5 : 0 }}>
            <Typography
              sx={{
                fontWeight: 600,
                mb: 1,
                fontSize: "1rem",
                color: "#333",
              }}
            >
              {key}
            </Typography>
            <Box sx={{ pl: 2.5, m: 0, listStyle: "none" }}>
              {value.map((item: any, idx: number) => (
                <Box
                  key={idx}
                  sx={{
                    mb: 1,
                    pl: 1.5,
                    position: "relative",
                    "&::before": {
                      content: '"•"',
                      position: "absolute",
                      left: 0,
                      color: "#646464",
                      fontSize: "1.2rem",
                      lineHeight: "1.5",
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.9rem",
                      lineHeight: 1.6,
                      color: "#444",
                    }}
                  >
                    {typeof item === "object"
                      ? JSON.stringify(item)
                      : String(item)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        );
      } else {
        return (
          <Box
            key={key}
            sx={{
              mb: 1.5,
              pl: 1.5,
              position: "relative",
              "&::before": {
                content: '"•"',
                position: "absolute",
                left: 0,
                color: "#646464",
                fontSize: "1.2rem",
                lineHeight: "1.5",
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.9rem",
                lineHeight: 1.6,
                color: "#444",
              }}
            >
              {String(value)}
            </Typography>
          </Box>
        );
      }
    });
  };

  return (
    <Fade in={true} timeout={500}>
      <Stack
        direction="column"
        display="flex"
        justifyContent="space-between"
        sx={{ height: "100%", minHeight: 0 }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {!hideHeader && (
            <Box
              sx={{
                color: "primary.main",
                p: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Article sx={{ fontSize: 18 }} />
              <Typography variant="subtitle2" fontWeight={600}>
                Document Summary
              </Typography>
            </Box>
          )}

          {selectedDocument && (
            <Box
              sx={{
                color: "primary.main",
                px: 1.5,
                py: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: "#fafafa",
              }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                📄
                {selectedDocument.unsignedDoc ||
                  selectedDocument.unsignedPdf ||
                  "Document"}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              backgroundColor: "#fafafa",

              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#d0d0d0",
                borderRadius: "3px",
                "&:hover": {
                  backgroundColor: "#b0b0b0",
                },
              },
            }}
          >
            {/* Announce readiness so screen-reader users know the summary
                arrived, without reading the whole document aloud (WCAG 4.1.3). */}
            <Box
              component="span"
              role="status"
              aria-live="polite"
              sx={{
                position: "absolute",
                width: "1px",
                height: "1px",
                overflow: "hidden",
                clip: "rect(0 0 0 0)",
                clipPath: "inset(50%)",
              }}
            >
              {summary ? "Summary ready" : ""}
            </Box>
            {summary ? (
              <Box>
                {summary.summary ? (
                  <Box
                    sx={{
                      backgroundColor: "#fafafa",
                      p: 3,
                      pt: 2,
                      borderRadius: 1,
                    }}
                  >
                    {renderSummaryContent(summary.summary)}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      py: 6,
                      opacity: 0.7,
                    }}
                  >
                    <ErrorOutlineIcon
                      sx={{ fontSize: "3rem", color: "#646464", mb: 2 }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        textAlign: "center",
                        color: "#646464",
                        fontWeight: 500,
                      }}
                    >
                      {/* The AI reports a per-document failure as `error` with no
                          `summary`; show its reason instead of the generic line. */}
                      {summary.error || "No summary data available"}
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    backgroundColor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2,
                  }}
                >
                  <Article sx={{ color: "white", fontSize: 28 }} />
                </Box>
                <Typography
                  variant="h6"
                  color="text.primary"
                  sx={{
                    textAlign: "center",
                    mb: 1,
                    fontWeight: 600,
                    fontSize: "1.125rem",
                  }}
                >
                  {signed ? "Summarize the signed document" : "Summarize before you send"}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    textAlign: "center",
                    fontSize: "0.875rem",
                    lineHeight: 1.6,
                    maxWidth: 280,
                    px: 2,
                  }}
                >
                  {signed
                    ? "Pick a document below and generate a plain-language summary of the signed copy, including who signed it."
                    : "Pick a document below and generate a plain-language summary of what your recipients will be signing."}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            pt: 2,
            pb: 1.5,
            px: 2,
            flexShrink: 0,
            borderTop: "1px solid #e0e0e0",
            backgroundColor: "white",
          }}
        >
          <Stack spacing={1.5}>
            <CustomAutoComplete
              id="document-autocomplete"
              label="Select document"
              placeHolder="Choose a document..."
              options={documentOptions}
              value={selectedOption}
              onChange={handleDocumentChange}
              getOptionLabel={getOptionLabel}
              freeSolo={false}
              clearIcon={true}
              filterOptions={filterOptions}
              // Same reason as the chat picker: the Autocomplete's popper is portalled at
              // zIndex.modal, which is below the mobile sheet this panel sits in.
              componentsProps={{
                popper: {
                  sx: { zIndex: (theme: any) => theme.zIndex.modal + 2 },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  paddingRight: "56px !important",
                  "& fieldset": {
                    borderColor: "#757575",
                    borderWidth: "1.5px",
                  },
                },
                "& .MuiAutocomplete-endAdornment": {
                  right: 6,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.875rem",
                  minWidth: 0,
                  textOverflow: "ellipsis",
                },
              }}
              textFieldVariant="outlined"
            />

            <Button
              variant="contained"
              fullWidth
              onClick={() => getDocumentSummary()}
              disabled={!selectedDocument || aiLoader}
              startIcon={
                aiLoader ? (
                  <CircularProgress
                    color="inherit"
                    size={16}
                    sx={{
                      color: "gray",
                    }}
                  />
                ) : (
                  <AutoAwesome sx={{ fontSize: 18 }} />
                )
              }
              sx={{
                color: "white",
                textTransform: "none",
                fontSize: "0.875rem",
                fontWeight: 600,
                py: 1.25,
                minHeight: 44,
                borderRadius: 1.5,
                whiteSpace: "normal",
                lineHeight: 1.3,
                overflow: "hidden",
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
                "&.Mui-disabled": {
                  color: "#646464",
                },
              }}
            >
              {aiLoader ? "Generating Summary..." : "Generate summary"}
            </Button>
          </Stack>
          <AIDisclaimer signed={signed} />
        </Box>
      </Stack>
    </Fade>
  );
};

export default Summarizer;
